import Link from "next/link";
import { ArrowRight, Utensils } from "lucide-react";
import { useFetchRestaurants } from "../places/restaurants/_hook";
import { Restaurant } from "@/types/places";
import { BlueRibbonLogos, MichelinLogos, PriceRangeBadge } from "../places/restaurants/_components/badges";
import { shortenAddress } from "@/utils/util";
import { SupportedLanguage } from "@/config/store/globalStore";
import { useLanguage } from "@/hooks/useLanguage";
import { useTranslation } from "@/hooks/useTranslation";
import Image from "next/image";
import { BLUR_URL, getUnifiedRestaurantsImages } from "@/utils/images";

  export default function FeaturedRestaurants() {
    const { data: expensiveRestaurants, loading: expensiveRestaurantsLoading } = useFetchRestaurants({
      award: 'b-3',
    });
    
    const { data: popularRestaurants, loading: popularRestaurantsLoading } = useFetchRestaurants({
      category: 'Korean Cuisine',
    });
  
  const { language } = useLanguage();
  const { t } = useTranslation();

  return (
    <>
    <section className="mb-20">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900">{t('featured_restaurants_title')}</h2>
        <Link
          href={`/${language}/places/restaurants`}
          className="flex items-center text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0"
        >
          {t('view_all')}
          <ArrowRight className="ml-1 w-4 h-4" />
        </Link>
      </div>
      
      {expensiveRestaurantsLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-6 gap-y-12">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="h-48 sm:h-52 md:h-48 lg:h-48 bg-gray-200 rounded-xl mb-4"></div>
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-6 gap-y-12">
          {expensiveRestaurants?.result
            ?.sort(() => Math.random() - 0.5)
            ?.slice(0, 8)
            ?.map((restaurant: Restaurant) => (
            <Link 
              key={restaurant.id}
              href={`/${language}/places/restaurants/${restaurant.id}`}
            >
              <div className="group cursor-pointer">
                <div className="mb-4 overflow-hidden rounded-xl relative h-48 sm:h-52 md:h-48 lg:h-48">
                  <Image 
                    src={getUnifiedRestaurantsImages(restaurant.id, restaurant.images.officialImages, restaurant.images.userImages)[0]}
                    alt={`${restaurant.name[language as SupportedLanguage]}-image`}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-300"
                    placeholder="blur"
                    blurDataURL={BLUR_URL}
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  />
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-semibold text-gray-900 group-hover:text-gray-600 transition-colors break-words">
                    {restaurant.name[language as SupportedLanguage]}
                  </h3>
                  <div className='flex items-center gap-1'>
                    {restaurant.michelin_info && (
                      <MichelinLogos count={restaurant.michelin_info.michelinAward} />
                    )}
                    {restaurant.blueribbon_info && (
                      <BlueRibbonLogos count={restaurant.blueribbon_info.ribbonType} />
                    )}
                  </div>
                </div>
                
                <div className="flex flex-col flex-wrap text-sm text-gray-600 mt-1">
                  <div>
                    {shortenAddress(restaurant.address?.[language as SupportedLanguage], language as SupportedLanguage)}
                  </div>
                  <div className="flex flex-wrap items-center text-sm text-gray-600 mt-1.5">
                    <span key={restaurant.food_category[language as SupportedLanguage]}>
                      {restaurant.food_category[language as SupportedLanguage]}
                    </span>
                    {restaurant.michelin_info && (
                      <>
                        &nbsp;•&nbsp;
                        <PriceRangeBadge priceRange={restaurant.michelin_info.priceSlug} />
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
    <section className="mb-20">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-2xl font-bold text-gray-900">{t('featured_local_restaurants_title')}</h2>
        <Link 
          href={`/${language}/places/restaurants`}
          className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          {t('view_all')}
          <ArrowRight className="ml-1 w-4 h-4" />
        </Link>
      </div>
      
      {popularRestaurantsLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-6 gap-y-12">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="h-48 sm:h-52 md:h-48 lg:h-48 bg-gray-200 rounded-xl mb-4"></div>
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-6 gap-y-12">
          {popularRestaurants?.result
            ?.sort(() => Math.random() - 0.5)
            ?.slice(0, 8)
            ?.map((restaurant: Restaurant) => (
            <Link 
              key={restaurant.id}
              href={`/${language}/places/restaurants/${restaurant.id}`}
            >
              <div className="group cursor-pointer">
                <div className="mb-4 overflow-hidden rounded-xl relative h-48 sm:h-52 md:h-48 lg:h-48">
                  <Image 
                    src={getUnifiedRestaurantsImages(restaurant.id, restaurant.images.officialImages, restaurant.images.userImages)[0]}
                    alt={`${restaurant.name[language as SupportedLanguage]}-image`}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-300"
                    placeholder="blur"
                    blurDataURL={BLUR_URL}
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  />
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-semibold text-gray-900 group-hover:text-gray-600 transition-colors break-words">
                    {restaurant.name[language as SupportedLanguage]}
                  </h3>
                  <div className='flex items-center gap-1'>
                    {restaurant.michelin_info && (
                      <MichelinLogos count={restaurant.michelin_info.michelinAward} />
                    )}
                    {restaurant.blueribbon_info && (
                      <BlueRibbonLogos count={restaurant.blueribbon_info.ribbonType} />
                    )}
                  </div>
                </div>
                
                <div className="flex flex-col flex-wrap text-sm text-gray-600 mt-1">
                  <div>
                    {shortenAddress(restaurant.address?.[language as SupportedLanguage], language as SupportedLanguage)}
                  </div>
                  <div className="flex flex-wrap items-center text-sm text-gray-600 mt-1.5">
                    <span key={restaurant.food_category[language as SupportedLanguage]}>
                      {restaurant.food_category[language as SupportedLanguage]}
                    </span>
                    &nbsp;•&nbsp;
                    <PriceRangeBadge priceRange={restaurant.michelin_info?.priceSlug || restaurant.blueribbon_info?.priceRange || 0 } />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
    </>
  );
} 